import { EmptyBox } from '@/components/EmptyBox'
import { Tooltip } from '@/components/Tooltip'
import { UpgradeLinkBtn } from '@/components/UpgradeLinkBtn/UpgradeLinkBtn'
import { FileExpirationTimeLimit } from '@/components/VipLimit/VipLimit'
import { commonUtils } from '@/helpers/commonUtils'
import { dayjsHelper } from '@/helpers/dayjsHelper'
import { ComponentFactory } from '@/mobxTools'
import { userService } from '@/services/userService'
import {
  CloseCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { Drawer, Popover, Spin } from 'antd'
import classNames from 'classnames'
import { debounce } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import style from './UploadHistoryModal.module.less'
import { UploadHistoryModalVm } from './UploadHistoryModalVm'

/** ReadHistoryModal 组件的 Props */
export type UploadHistoryModalProps = {
  open: boolean
  currentFileId: number | null
  onPdfFileChanged: (file: ServerDataTypes.PDF.PdfProHistoryInfo) => void
  onClose: () => void
}

/**
 * @author TODO
 * (在这里对组件进行说明)
 */
export const UploadHistoryModal = ComponentFactory.createComponent(
  'UploadHistoryModal',
  UploadHistoryModalVm,
  (vm) => {
    const { open, onPdfFileChanged, onClose, currentFileId } = vm.props
    const inputRef = useRef<HTMLInputElement>(null)

    const initLoading = vm.historyListLoading && vm.historyListData.length === 0

    useEffect(() => {
      if (open) {
        setTimeout(() => {
          inputRef.current?.focus()
        }, 100)

        if (vm.loadingEle) {
          // console.log('@@@loadingEle:', vm.loadingEle)
          const _handleEntry = async (entry: IntersectionObserverEntry) => {
            // console.log('@@@entry:', {
            //   historyListLoading: vm.historyListLoading,
            //   hasMore: vm.hasMore,
            //   isIntersecting: entry.isIntersecting,
            // })
            if (!vm.historyListLoading && vm.hasMore && entry.isIntersecting) {
              vm.loadMoreData()
            }
          }
          const handleEntry = debounce(_handleEntry, 100)

          vm.observer = new IntersectionObserver(
            (entries: IntersectionObserverEntry[]) => {
              handleEntry(entries[0])
            },
            {
              root: vm.scrollBox,
              threshold: 0.1,
              rootMargin: '0px 0px 100px 0px',
            }
          )

          vm.observer.observe(vm.loadingEle)
        }
      } else {
        vm.historyListData = []
        vm.hasMore = true
        vm.historyListLoading = false
        vm.keyword = ''
      }

      return () => {
        if (vm.observer) {
          vm.observer.disconnect()
        }
      }
    }, [open])

    return (
      <>
        <Drawer
          autoFocus={false}
          title={null}
          destroyOnClose={true}
          placement='left'
          closable={false}
          onClose={() => {
            onClose()
          }}
          open={open}
          key='left'
          width={800}
          maskClosable={true}
          styles={{
            body: {
              padding: 0,
            },
            mask: {
              backgroundColor: 'transparent',
            },
          }}
        >
          <div
            className={classNames(style.readHistoryModal)}
            onClick={(e) => {
              e.stopPropagation()
            }}
          >
            <div className={classNames(style.header)}>
              <div className={style.tabBox}>
                <div className={classNames(style.tab, style.active)}>
                  单个文件翻译记录
                  <div className={classNames(style.line)}></div>
                </div>
              </div>
              <div
                className={classNames(style.right)}
                onClick={() => {
                  onClose()
                }}
              >
                <span>收起</span>
                <img
                  className={classNames(style.icon)}
                  src={commonUtils.getStaticFile('pdf/icon__history-close.png')}
                />
              </div>
            </div>

            <div className={classNames(style.fileSaveTimeBox)}>
              <span>
                当前版本为：{userService.vipVersionName}。上传文件将被安全保留
                <Popover
                  content={
                    <div style={{ maxWidth: '320px' }}>
                      <p>
                        您上传的文件（包括 PDF、Word
                        等）会被存储在专属的安全服务器中，以方便您随时访问&下载
                      </p>
                      <br />
                      <p>
                        基于文件安全及存储成本考虑，文件在一段时间后将被清理。我们会为更高级会员提供更长的文件保留时长
                      </p>
                    </div>
                  }
                  placement='right'
                  getPopupContainer={(el) => el.parentElement!}
                >
                  <img
                    className={style.warningIcon}
                    src={commonUtils.getStaticFile('common/icon__warning.png')}
                  />
                </Popover>
                ：{userService.getPlanFileMaxRule('pdf_pro').fileSaveDay} 天
              </span>
              {userService.vipVersion !== 'ultra' && (
                <UpgradeLinkBtn
                  styleCSS={{ marginLeft: 12 }}
                  version={userService.vipVersion === 'free' ? 'max' : 'ultra'}
                  feature='fileSaveTime'
                  source='file_save_time_pdf_pro_history'
                  text={
                    userService.vipVersion === 'free'
                      ? '立即升级，解锁更长文件保留时间'
                      : '升级Ultra会员，解锁6倍文件保留时间'
                  }
                />
              )}
            </div>

            <div className={classNames(style.searchBox)}>
              <SearchOutlined className={style.icon} />
              <input
                ref={inputRef}
                type='text'
                className={classNames(style.input)}
                placeholder='请输入要搜索的内容'
                value={vm.keyword}
                onKeyDown={(e) => e.stopPropagation()}
                onChange={(e) => {
                  vm.keyword = e.target.value

                  vm.handleSearch()
                }}
              />

              {vm.keyword.length > 0 && (
                <CloseCircleOutlined
                  className={style.clear}
                  onClick={() => {
                    vm.keyword = ''
                    vm.handleSearch()
                  }}
                />
              )}
            </div>

            <div className={classNames(style.scrollBox)} ref={(node) => (vm.scrollBox = node)}>
              {initLoading && (
                <div className={classNames(style.init)}>
                  <Spin></Spin>
                </div>
              )}
              {vm.noHistoryData && <EmptyBox desc='暂无历史记录～' />}

              {vm.historyListData.map((item) => {
                const fileSize =
                  item.fileSize / 1024 / 1024 > 1
                    ? (item.fileSize / 1024 / 1024).toFixed(1) + 'M'
                    : (item.fileSize / 1024).toFixed(1) + 'KB'

                let pageInfo = ''
                if (item.pages.length > 0) {
                  const start = item.pages[0]
                  const end = item.pages[item.pages.length - 1]
                  pageInfo = `${end - start + 1}`
                } else {
                  pageInfo = `${item.pageNum}`
                }
                const expirationTime = `${userService.calcFileExpireDayByUploadTime(
                  item.uploadTime,
                  'pdf_pro'
                )} 天`

                return (
                  <div
                    key={item.fileId}
                    className={classNames(style.listItem, {
                      [style.active]: item.fileId === currentFileId,
                    })}
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      onClose()
                      onPdfFileChanged(item)
                    }}
                  >
                    <img
                      className={classNames(style.imgIcon)}
                      src={commonUtils.getStaticFile('pdf/icon__large-pdf.png')}
                    />
                    <div className={classNames(style.itemContent)}>
                      <RenderElementWithTooltip
                        highlightName={item.highlightName}
                        fileName={item.fileName}
                      />
                      <div className={classNames(style.detail)}>
                        <span className={classNames(style.time)}>
                          {dayjsHelper.formatTime(item.uploadTime * 1000, 'YYYY-MM-DD HH:mm')}
                        </span>
                        <span className={classNames(style.size)}>{fileSize}</span>
                      </div>
                    </div>

                    <div className={classNames(style.actionBox)}>
                      <div
                        className={classNames(style.delete)}
                        onClick={(e) => {
                          e.stopPropagation()
                          vm.handleDeleteHistoryItem(item)
                        }}
                      >
                        <Tooltip title='删除'>
                          <DeleteOutlined className={classNames(style.img)} />
                        </Tooltip>
                      </div>
                      <div
                        className={classNames(style.delete)}
                        onClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          onPdfFileChanged(item)
                          onClose()
                        }}
                      >
                        <Tooltip title='下载'>
                          <DownloadOutlined className={classNames(style.img)} />
                        </Tooltip>
                      </div>
                    </div>
                    <div className={classNames(style.expirationTime)}>
                      共翻译 {pageInfo} 页｜
                      <FileExpirationTimeLimit fileSaveTime={expirationTime} />
                    </div>
                  </div>
                )
              })}

              {!vm.historyListLoading && !vm.hasMore && (
                <>
                  {vm.historyListData.length > 6 && (
                    <p className={classNames(style.noMore)}>没有更多了～</p>
                  )}
                </>
              )}

              <div
                ref={(node) => (vm.loadingEle = node)}
                className={classNames(style.loading, {
                  [style.visible]: vm.historyListLoading && !initLoading,
                })}
              >
                正在加载中...
              </div>
            </div>
          </div>
        </Drawer>
      </>
    )
  }
)

const RenderElementWithTooltip = ({
  fileName,
  highlightName,
}: {
  fileName: string
  highlightName: string
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [showTooltip, setShowToolTip] = useState(false)

  useEffect(() => {
    if (ref.current) {
      const element = ref.current
      if (element.scrollWidth > element.clientWidth) {
        setShowToolTip(true)
      } else {
        setShowToolTip(false)
      }
    }
  }, [])

  if (showTooltip) {
    return (
      <Tooltip title={fileName}>
        <div
          ref={ref}
          className={classNames(style.name)}
          dangerouslySetInnerHTML={{ __html: highlightName }}
        ></div>
      </Tooltip>
    )
  }

  return (
    <div
      className={classNames(style.name)}
      ref={ref}
      dangerouslySetInnerHTML={{ __html: highlightName }}
    ></div>
  )
}
